import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Components/Header';
import Footer from './Components/Footer';

const normalizeListing = (listing = {}) => ({
  ...listing,
  id: listing.id ?? listing._id ?? listing.slug ?? listing.uuid ?? listing.businessId,
  name: listing.name ?? listing.businessName ?? listing.companyName ?? listing.title ?? listing.displayName,
  title: listing.title ?? listing.name ?? listing.businessName ?? listing.companyName,
  description: listing.description ?? listing.about ?? listing.bio ?? '',
  price: Number(listing.price ?? listing.startingPrice ?? listing.minPrice ?? 0),
  rating: Number(listing.rating ?? listing.averageRating ?? 0),
  services: Array.isArray(listing.services) ? listing.services : (Array.isArray(listing.serviceTypes) ? listing.serviceTypes : []),
  tags: Array.isArray(listing.tags) ? listing.tags : (Array.isArray(listing.categories) ? listing.categories : []),
  coverImage: listing.coverImage.url ?? '',
  zipCode: listing.zipCode ?? listing.zip ?? listing.postalCode ?? '',
  city: listing.city ?? listing.location ?? '',
  isVerified: Boolean(listing.isVerified ?? listing.verified ?? listing.is_verified)
});

const extractListings = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.listings)) return data.listings;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export default function Listings() {
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') || "";

  // State for listings
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [query, setQuery] = useState(search || "");

  // Filter states
  const [selectedServices, setSelectedServices] = useState({
    Residential: true,
    Commercial: true,
    Automotive: true,
    Emergency: true
  });
  const [maxPrice, setMaxPrice] = useState(500);
  const [sortBy, setSortBy] = useState('Recommended');
  const [distance, setDistance] = useState('Within 10 miles');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const listingsPerPage = 30; // Changed from 6 to 30

  // Fetch listings
  useEffect(() => {
    const fetchListings = async () => {
      if (search) {
        setQuery(search);
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const searchQuery = query ? `?search=${encodeURIComponent(query.trim())}` : '';
      const candidateUrls = [`${backendUrl}/api/listings${searchQuery}`, `${backendUrl}/listings${searchQuery}`];

      try {
        let data = null;

        for (const url of candidateUrls) {
          const res = await fetch(url);
          if (res.ok) {
            data = await res.json();
            break;
          }
        }

        if (data) {
          const listings = extractListings(data).map(normalizeListing);
          setAllListings(listings);
          setFilteredListings(listings);
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Failed to fetch listings', err);
      }
    };

    fetchListings();
  }, [search]);

  // Toggle service checkbox
  const handleServiceChange = (service) => {
    setSelectedServices(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle query change
  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  // Apply manual filter (from sidebar)
  const applyFilters = () => {
    const filtered = allListings.filter((l) =>
      l.title.toLowerCase().includes(query.toLowerCase()) ||
      l.zipCode === query
    );
    setFilteredListings(filtered);
    setCurrentPage(1);
  };

  // Filtered & Sorted locksmiths with pagination
  const filteredAndSortedListings = useMemo(() => {
    return filteredListings.filter(pro => {
      const matchesService = (pro.services?.length
        ? pro.services.some((srv) => selectedServices[String(srv)] ?? selectedServices[String(srv).toLowerCase()])
        : true) ?? true;

      const matchesPrice = Number(pro.price || 0) <= maxPrice;

      const queryLower = query.toLowerCase();
      const matchesSearch = query === '' ||
        String(pro.name || pro.title || '').toLowerCase().includes(queryLower) ||
        String(pro.description || '').toLowerCase().includes(queryLower) ||
        String(pro.zipCode || pro.zip || pro.postalCode || '').toLowerCase() === queryLower ||
        String(pro.zipCode || pro.zip || pro.postalCode || '').toLowerCase().includes(queryLower) ||
        String(pro.city || pro.location || '').toLowerCase().includes(queryLower) ||
        (pro.tags || []).some((tag) => String(tag).toLowerCase().includes(queryLower));

      return matchesService && matchesPrice && matchesSearch;
    }).sort((a, b) => {
      if (sortBy === 'Top Rated') {
        return (b.rating || 0) - (a.rating || 0);
      }
      if (sortBy === 'Price: Low to High') {
        return (a.price || 0) - (b.price || 0);
      }
      if (sortBy === 'Price: High to Low') {
        return (b.price || 0) - (a.price || 0);
      }
      return 0; // Default Recommended (original order)
    });
  }, [filteredListings, selectedServices, maxPrice, query, sortBy]);

  // Pagination calculations
  const indexOfLastListing = currentPage * listingsPerPage;
  const indexOfFirstListing = indexOfLastListing - listingsPerPage;
  const currentListings = filteredAndSortedListings.slice(
    indexOfFirstListing,
    indexOfLastListing
  );
  const totalPages = Math.ceil(filteredAndSortedListings.length / listingsPerPage);

  // Pagination functions
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      if (currentPage <= 2) {
        endPage = Math.min(totalPages - 1, 4);
      }
      if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  return (
    <div className="min-h-screen flex flex-col text-on-surface bg-background pt-16">
      <Header />

      <div className="flex flex-1 max-w-container-max mx-auto w-full">
        {/* Main Content */}
        <main className="flex-1 px-margin-mobile md:px-gutter py-8 bg-background">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="font-headline-lg text-headline-lg text-primary">Verified Professionals</h1>
              <p className="text-body-lg text-on-surface-variant">
                Showing {filteredAndSortedListings.length} certified locksmiths in your area
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Search input inline */}
              <div className="flex items-center bg-white border border-outline-variant/60 rounded-lg px-3 py-1.5 w-64 shadow-sm">
                <span className="material-symbols-outlined text-primary/60 mr-2 text-[20px]">search</span>
                <input
                  type="text"
                  placeholder="Search locksmiths..."
                  value={query}
                  onChange={handleQueryChange}
                  className="bg-transparent border-none p-0 focus:ring-0 text-body-md w-full text-on-surface"
                />
              </div>
            </div>
          </div>

          {/* Business Listing Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {currentListings.map((pro) => (
              <div
                key={pro.id}
                className="bg-white rounded-xl border border-outline-variant hover:shadow-lg transition-all duration-300 group overflow-hidden flex flex-col hover:-translate-y-1"
              >
                <div className="h-48 relative overflow-hidden">
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    alt={pro.name || pro.title}
                    src={pro.coverImage}
                  />
                  {pro.isVerified && (
                    <div className="absolute top-4 left-4 bg-primary text-on-primary px-3 py-1 rounded-full text-[12px] font-bold flex items-center gap-1 shadow-sm">
                      <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                        verified
                      </span>
                      VERIFIED
                    </div>
                  )}
                </div>
                <div className="p-6 flex flex-1 flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <Link to={`/profile/${pro.id}`} className="font-headline-md text-[20px] text-primary group-hover:text-secondary-container transition-colors">
                        {pro.name || pro.title}
                      </Link>
                      {pro.rating && (
                        <div className="flex items-center text-secondary-container shrink-0">
                          <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                            star
                          </span>
                          <span className="font-bold ml-1 text-on-surface">{pro.rating}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-on-surface-variant text-body-md mb-4 line-clamp-2">
                      {pro.description}
                    </p>
                    {pro.zipCode && (
                      <p className="text-on-surface-variant text-body-md mb-4 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[18px]">location_on</span>
                        <span>{pro.zipCode}{pro.city ? `, ${pro.city}` : ''}</span>
                      </p>
                    )}
                    {pro.tags && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {pro.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 rounded-full text-[12px] font-bold ${tag.includes('Emergency') || tag.includes('Response')
                              ? 'bg-secondary-fixed text-on-secondary-fixed-variant'
                              : 'bg-surface-container text-primary'
                              }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-outline-variant pt-4 flex items-center justify-between">
                    
                    <Link
                      to={`/profile/${pro.id}`}
                      className="bg-primary text-on-primary px-6 py-2.5 rounded-lg font-label-md hover:bg-primary-container transition-all active:scale-[0.97]"
                    >
                      View Profile
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredAndSortedListings.length === 0 && (
            <div className="text-center py-16 bg-white border border-dashed border-outline-variant/60 rounded-2xl max-w-lg mx-auto mt-12 shadow-sm">
              <span className="material-symbols-outlined text-[48px] text-on-surface-variant mb-4">search_off</span>
              <h3 className="font-headline-md text-primary mb-2">No Locksmiths Found</h3>
              <p className="text-on-surface-variant max-w-sm mx-auto">
                No verified professionals matched your current filter criteria. Try adjusting your price range or selected service types.
              </p>
             
            </div>
          )}

          {/* Pagination */}
          {filteredAndSortedListings.length > 0 && (
            <div className="flex flex-col items-center mt-12 mb-8 gap-4">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {/* Previous Button */}
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg border ${currentPage === 1 
                    ? 'border-outline-variant/40 text-on-surface-variant/40 cursor-not-allowed' 
                    : 'border-outline-variant hover:bg-surface-container transition-colors'}`}
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="w-10 h-10 flex items-center justify-center text-on-surface-variant">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${currentPage === page 
                        ? 'bg-primary text-on-primary font-bold' 
                        : 'border border-outline-variant hover:bg-surface-container'}`}
                    >
                      {page}
                    </button>
                  )
                ))}

                {/* Next Button */}
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg border ${currentPage === totalPages 
                    ? 'border-outline-variant/40 text-on-surface-variant/40 cursor-not-allowed' 
                    : 'border-outline-variant hover:bg-surface-container transition-colors'}`}
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>

              {/* Pagination Info */}
              <div className="text-sm text-on-surface-variant">
                Showing {indexOfFirstListing + 1} to {Math.min(indexOfLastListing, filteredAndSortedListings.length)} of {filteredAndSortedListings.length} results
              </div>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}